import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';
import map from 'lodash/map';
import includes from 'lodash/includes';
import { useTranslation } from 'react-i18next';
import { EventTypes, EventTypeAssetApplicability } from '../constants';
import Tippy from '@tippy.js/react';
import { IconInfo } from 'svgIcons/MotionPortalIcons';


const EventTypeFilter = ({
    selectedStates,
    selectState,
    assetType,
    disabled = false
}) => {
    const { t: translate } = useTranslation();
    const eventTypes = EventTypes.filter(
        eventType => eventType.applicableTo === assetType ||
            eventType.applicableTo === EventTypeAssetApplicability.ALL);

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => setIsVisible(false);
        const removeScrollListener = () => window.removeEventListener('scroll', handleScroll, true);
        window.addEventListener('scroll', handleScroll, true);
        return removeScrollListener;
    }, []);

    useEffect(() => {
        if (!isVisible) {
            return undefined;
        }

        const pointerEvents = ['mousedown', 'touchstart'];
        const closeTooltip = ({ target }) => !target?.closest?.('.event-type-filter') && setIsVisible(false);
        const closeOnEsc = ({ key }) => key === 'Escape' && setIsVisible(false);

        pointerEvents.forEach((eventName) => document.addEventListener(eventName, closeTooltip));
        document.addEventListener('keydown', closeOnEsc);

        return () => {
            pointerEvents.forEach((eventName) => document.removeEventListener(eventName, closeTooltip));
            document.removeEventListener('keydown', closeOnEsc);
        };
    }, [isVisible]);

    const renderEventTypeLabels = () => {
        return map(eventTypes, (eventType, index) =>
            <Row key={index}>
                <Col md='auto' className='pr-1'>
                    {eventType.icon}
                </Col>
                <Col className='event-list-filter-tippy-label pl-1'>
                    {translate(eventType.label)}
                </Col>
            </Row>
        );
    };

    const renderTooltipContent = () => {
        return <Container>
            <Row className='event-list-filter-tippy-title'>
                <Col>{translate('ABB.Powertrain.Frontend.eventListFilterEventTypesLbl')}</Col>
            </Row>
            {renderEventTypeLabels()}
        </Container>;
    };

    // Helper function to render a single event type button
    const renderEventTypeButton = (eventType) => {
        const isSelected = includes(selectedStates, eventType.typeID);
        const buttonClass = `event-type${isSelected ? ' selected' : ''}`;
        const handleClick = () => {
            if (!disabled) {
                setIsVisible(false);
                selectState(eventType.typeID);
            }
        };

        return (
            <button
                className={buttonClass}
                onClick={handleClick}
                key={eventType.typeID}
            >
                {eventType.icon}
            </button>
        );
    };

    const renderEventTypeButtons = () => {
        return map(eventTypes, (eventType) => renderEventTypeButton(eventType));
    };

    return (
        <div className='event-type-filter'>
            <div>{translate('ABB.Powertrain.Frontend.eventListFilterEventTypeLbl')}</div>
            <div className='event-types'>
                {renderEventTypeButtons()}
                <Tippy
                    content={renderTooltipContent()}
                    className='event-type-filter-info-tippy'
                    interactive={true}
                    animation='scale'
                    theme='light-border'
                    trigger='click'
                    visible={isVisible}
                    onClickOutside={() => setIsVisible(false)}
                    placement='bottom'
                    popperOptions={{
                        modifiers: {
                            preventOverflow: {
                                escapeWithReference: true
                            }
                        }
                    }}
                >
                    <button
                        className='info-icon-wrapper'
                        onClick={() => setIsVisible(v => !v)}
                        type='button'>
                        <IconInfo color={'black'} />
                    </button>
                </Tippy>
            </div>
        </div>
    );
};

EventTypeFilter.propTypes = {
    selectedStates: PropTypes.array,
    selectState: PropTypes.func,
    assetType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    disabled: PropTypes.bool
};

export default EventTypeFilter;
